import { StrapiFilterParams } from '@monoimin/sb-types';

import { isNumericString } from '@monoimin/utils/helpers';

import {
  ESTATE_UNIT_FILTER_KEYS,
  ESTATE_RANGE_FROM_FILTER_KEYS,
  STRAPI_FILTER_OPERATORS,
  FILTER_VARIANTS,
} from '../constants';

import { prepareParamsCreator } from '../utils/filters/prepareParamsCreator';
import { defineParamsCreator } from '../utils/filters/defineParamsCreator';
import { createBetweenFilterPart } from '../utils/filters/createBetweenFilterPart';
import { createFromToFilterPart } from '../utils/filters/createFromToFilterPart';
import { priceFormatter } from '../utils/filters/priceFormatter';
import { metersFormatter } from '../utils/filters/metersFormatter';

export const FILTER_CONFIGS_UTILS = {
  [ESTATE_UNIT_FILTER_KEYS.COUNTRY]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        [ESTATE_UNIT_FILTER_KEYS.COUNTRY, 'slug'],
        STRAPI_FILTER_OPERATORS.IN
      )
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.CITY]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        [ESTATE_UNIT_FILTER_KEYS.CITY, 'slug'],
        STRAPI_FILTER_OPERATORS.IN
      )
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.OBJECT_TYPE]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        ESTATE_UNIT_FILTER_KEYS.OBJECT_TYPE,
        STRAPI_FILTER_OPERATORS.CONTAINSI
      )
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.BEDROOMS]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator('bedrooms_number', STRAPI_FILTER_OPERATORS.CONTAINSI)
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.DEVELOPER]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        [ESTATE_UNIT_FILTER_KEYS.DEVELOPER, 'title'],
        STRAPI_FILTER_OPERATORS.STARTS_WITHI
      )
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.ESTATE_TYPE]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        ESTATE_UNIT_FILTER_KEYS.ESTATE_TYPE,
        STRAPI_FILTER_OPERATORS.CONTAINSI
      )
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.OBJECT_STAGE]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        ESTATE_UNIT_FILTER_KEYS.OBJECT_STAGE,
        STRAPI_FILTER_OPERATORS.CONTAINSI
      )
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.DESIGN_FEATURE]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator('registration_features', STRAPI_FILTER_OPERATORS.IN)
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.SEARCH]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.INPUT,
      (value) => {
        if (!value) {
          return undefined;
        }

        const params: Array<StrapiFilterParams> = [
          {
            title: {
              [STRAPI_FILTER_OPERATORS.CONTAINSI]: value,
            },
          },
          {
            custom_id: value,
          },
        ];

        if (typeof value === 'string' && isNumericString(value)) {
          params.push({
            id: value,
          });
        }

        return {
          [STRAPI_FILTER_OPERATORS.OR]: params,
        };
      }
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.VIEW]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        ESTATE_UNIT_FILTER_KEYS.VIEW,
        STRAPI_FILTER_OPERATORS.CONTAINSI
      )
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.IS_ISLAND]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        ESTATE_UNIT_FILTER_KEYS.IS_ISLAND,
        STRAPI_FILTER_OPERATORS.IN
      )
    ),
  },

  [ESTATE_UNIT_FILTER_KEYS.BATHROOMS]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        ESTATE_UNIT_FILTER_KEYS.BATHROOMS,
        STRAPI_FILTER_OPERATORS.CONTAINSI
      )
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.FURNITURE]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        ESTATE_UNIT_FILTER_KEYS.FURNITURE,
        STRAPI_FILTER_OPERATORS.IN
      )
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.PARKING]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        ESTATE_UNIT_FILTER_KEYS.PARKING,
        STRAPI_FILTER_OPERATORS.IN
      )
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.BEST_OFFER]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator('is_favorable', STRAPI_FILTER_OPERATORS.IN)
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.ELIGIBLE_FOR_PROGRAM]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        ESTATE_UNIT_FILTER_KEYS.ELIGIBLE_FOR_PROGRAM,
        STRAPI_FILTER_OPERATORS.IN
      )
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.COST_FOR_GR]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.OPTION,
      defineParamsCreator(
        ESTATE_UNIT_FILTER_KEYS.COST_FOR_GR,
        STRAPI_FILTER_OPERATORS.IN
      )
    ),
  },
  [ESTATE_RANGE_FROM_FILTER_KEYS.PRICE_FROM]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.RANGE,
      (value) => {
        if (!Array.isArray(value)) {
          return undefined;
        }

        const [from, to] = value;

        const filterPart = createFromToFilterPart('price', from, to);

        if (!filterPart) {
          return undefined;
        }

        return {
          [STRAPI_FILTER_OPERATORS.AND]: [filterPart],
        };
      }
    ),
    valueFormatter: priceFormatter,
  },
  [ESTATE_RANGE_FROM_FILTER_KEYS.RENTAL_YIELD_FROM]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.INPUT,
      (value) => {
        const filterPart = createFromToFilterPart('apr', value, null);

        if (!filterPart) {
          return undefined;
        }

        return {
          [STRAPI_FILTER_OPERATORS.AND]: [filterPart],
        };
      }
    ),
  },
  [ESTATE_UNIT_FILTER_KEYS.OBJECT_CATEGORY]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.SELECT,
      defineParamsCreator(
        ESTATE_UNIT_FILTER_KEYS.OBJECT_CATEGORY,
        STRAPI_FILTER_OPERATORS.CONTAINSI
      )
    ),
  },
  [ESTATE_RANGE_FROM_FILTER_KEYS.OCEAN_FROM]: {
    filterParamsCreator: prepareParamsCreator(
      FILTER_VARIANTS.RANGE,
      (value) => {
        if (!Array.isArray(value)) {
          return undefined;
        }

        const [from, to] = value;

        const filterPart = createBetweenFilterPart('to_ocean', from, to);

        if (!filterPart) {
          return undefined;
        }

        return {
          [STRAPI_FILTER_OPERATORS.AND]: [filterPart],
        };
      }
    ),
    valueFormatter: metersFormatter,
  },
} as const;
